import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationCenter = ({ isOpen = false, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Simulate real-time notifications
    const mockNotifications = [
      {
        id: 1,
        type: 'bid_alert',
        title: 'New High Bid Received',
        message: 'Auto insurance lead #AI-2024-0818-001 received bid of $45.50',
        timestamp: new Date(Date.now() - 2 * 60 * 1000),
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'auction_result',
        title: 'Auction Completed',
        message: 'Lead #HO-2024-0818-003 sold for $32.75 to Premium Insurance Co.',
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        read: false,
        priority: 'medium'
      },
      {
        id: 3,
        type: 'system_alert',
        title: 'Integration Status',
        message: 'CRM sync completed successfully. 47 leads processed.',
        timestamp: new Date(Date.now() - 45 * 60 * 1000),
        read: true,
        priority: 'low'
      },
      {
        id: 4,
        type: 'revenue_milestone',
        title: 'Revenue Milestone',
        message: 'Daily revenue target of $2,500 achieved!',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: true,
        priority: 'medium'
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'bid_alert':
        return 'TrendingUp';
      case 'auction_result':
        return 'Gavel';
      case 'system_alert':
        return 'Settings';
      case 'revenue_milestone':
        return 'Target';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp?.toLocaleDateString();
  };

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification?.read;
    return notification?.type === filter;
  });

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev?.map(notification =>
        notification?.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev?.map(notification => ({ ...notification, read: true }))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1100 lg:relative lg:inset-auto">
      {/* Mobile Overlay */}
      <div 
        className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Notification Panel */}
      <div className="fixed right-0 top-16 lg:absolute lg:right-0 lg:top-full lg:mt-2 w-full max-w-sm bg-card border border-border rounded-lg shadow-modal lg:shadow-card">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={20} className="text-foreground" />
            <h3 className="font-semibold text-foreground">Notifications</h3>
            {notifications?.filter(n => !n?.read)?.length > 0 && (
              <span className="px-2 py-0.5 bg-error text-error-foreground text-xs rounded-full">
                {notifications?.filter(n => !n?.read)?.length}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs"
            >
              Mark all read
            </Button>
            <button
              onClick={onClose}
              className="p-1 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-border">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'bid_alert', label: 'Bids' },
            { key: 'system_alert', label: 'System' }
          ]?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setFilter(tab?.key)}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-smooth ${
                filter === tab?.key
                  ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto">
          {filteredNotifications?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Bell" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`p-4 hover:bg-muted/50 transition-smooth cursor-pointer ${
                    !notification?.read ? 'bg-accent/5' : ''
                  }`}
                  onClick={() => markAsRead(notification?.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg bg-muted ${getNotificationColor(notification?.priority)}`}>
                      <Icon name={getNotificationIcon(notification?.type)} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-foreground truncate">
                          {notification?.title}
                        </h4>
                        {!notification?.read && (
                          <div className="w-2 h-2 bg-primary rounded-full ml-2 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {notification?.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatTimestamp(notification?.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            iconName="ExternalLink"
            iconPosition="right"
          >
            View All Notifications
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;